import React, { useEffect, useState, createElement } from 'react';
import { Clock, Download, Search, User, FileText, Mail, Tag, Users, Settings, Send, ChevronDown, ChevronRight, ChevronLeft, RefreshCw, Filter, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auditLogApi, AuditLogRecord, CreatorFilter } from '../services/creatorApi';
import { toast } from 'sonner';
const actionColors: Record<string, {
  bg: string;
  text: string;
}> = {
  created: {
    bg: 'bg-green-100',
    text: 'text-green-800'
  },
  updated: {
    bg: 'bg-blue-100',
    text: 'text-blue-800'
  },
  deleted: {
    bg: 'bg-red-100',
    text: 'text-red-800'
  },
  status_changed: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800'
  },
  duplicated: {
    bg: 'bg-purple-100',
    text: 'text-purple-800'
  },
  archived: {
    bg: 'bg-gray-100',
    text: 'text-gray-800'
  },
  restored: {
    bg: 'bg-teal-100',
    text: 'text-teal-800'
  }
};
const entityIcons: Record<string, any> = {
  form: FileText,
  template: Mail,
  newsletter: Mail,
  campaign: Send,
  user: User,
  tag: Tag,
  form_config: Settings,
  workspace: Users,
  subscription: Users,
  submission: FileText,
  contact: Users,
  role: Settings,
  audience: Users
};
// Hardcoded options for server-side filtering
const ACTION_OPTIONS = [{
  value: 'created',
  label: 'Created'
}, {
  value: 'updated',
  label: 'Updated'
}, {
  value: 'deleted',
  label: 'Deleted'
}, {
  value: 'status_changed',
  label: 'Status Changed'
}, {
  value: 'duplicated',
  label: 'Duplicated'
}, {
  value: 'archived',
  label: 'Archived'
}, {
  value: 'restored',
  label: 'Restored'
}];
const ENTITY_OPTIONS = [{
  value: 'form',
  label: 'Form'
}, {
  value: 'template',
  label: 'Template'
}, {
  value: 'newsletter',
  label: 'Newsletter'
}, {
  value: 'campaign',
  label: 'Campaign'
}, {
  value: 'user',
  label: 'User'
}, {
  value: 'tag',
  label: 'Tag'
}, {
  value: 'form_config',
  label: 'Form Config'
}, {
  value: 'workspace',
  label: 'Workspace'
}, {
  value: 'subscription',
  label: 'Subscription'
}, {
  value: 'submission',
  label: 'Submission'
}, {
  value: 'contact',
  label: 'Contact'
}, {
  value: 'role',
  label: 'Role'
}, {
  value: 'audience',
  label: 'Audience'
}];
/** Convert unix timestamp (seconds) to Date */
function parseUnixTimestamp(ts?: string): Date | null {
  if (!ts) return null;
  const num = Number(ts);
  if (isNaN(num)) return null;
  return new Date(num * 1000);
}
/** Format a unix timestamp for display */
function formatTimestamp(ts?: string): {
  date: string;
  time: string;
} {
  const d = parseUnixTimestamp(ts);
  if (!d) return {
    date: '',
    time: ''
  };
  return {
    date: d.toLocaleDateString(),
    time: d.toLocaleTimeString()
  };
}
const PAGE_SIZE = 200;
export function AuditLog() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [auditLogs, setAuditLogs] = useState<AuditLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pending filter state (UI inputs, not yet applied)
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterEntity, setFilterEntity] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Active (committed) filter state — what's actually sent to the server
  const [activeSearch, setActiveSearch] = useState('');
  const [activeAction, setActiveAction] = useState<string>('all');
  const [activeEntity, setActiveEntity] = useState<string>('all');
  const [activeDateFrom, setActiveDateFrom] = useState('');
  const [activeDateTo, setActiveDateTo] = useState('');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;
  // Server-side stat counts
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [statusChangedCount, setStatusChangedCount] = useState(0);
  // UI state
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const buildScopeFilters = (): CreatorFilter[] => {
    const filters: CreatorFilter[] = [];
    filters.push({
      field: 'Team',
      value: currentWorkspace.name
    });
    if (currentSubRegion) {
      filters.push({
        field: 'Region',
        value: currentSubRegion
      });
    }
    return filters;
  };
  const buildAllFilters = (action: string, entity: string, search: string, dFrom: string, dTo: string): CreatorFilter[] => {
    const filters: CreatorFilter[] = [...buildScopeFilters()];
    if (action !== 'all') {
      filters.push({
        field: 'Action_Type',
        value: action
      });
    }
    if (entity !== 'all') {
      filters.push({
        field: 'Entity_Type',
        value: entity
      });
    }
    if (dFrom) {
      const fromDate = new Date(dFrom);
      fromDate.setHours(0, 0, 0, 0);
      filters.push({
        field: 'created',
        value: String(Math.floor(fromDate.getTime() / 1000)),
        op: '>='
      });
    }
    if (dTo) {
      const toDate = new Date(dTo);
      toDate.setHours(23, 59, 59, 999);
      filters.push({
        field: 'created',
        value: String(Math.floor(toDate.getTime() / 1000)),
        op: '<='
      });
    }
    if (search.trim()) {
      filters.push({
        fields: ['Entity_Name', 'Summary', 'User_Name'],
        value: search.trim()
      });
    }
    return filters;
  };
  const loadAuditLogs = async (page: number = 1, action: string = activeAction, entity: string = activeEntity, search: string = activeSearch, dFrom: string = activeDateFrom, dTo: string = activeDateTo) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const dataFilters = buildAllFilters(action, entity, search, dFrom, dTo);
      // Counts use all filters EXCEPT Action_Type (each count adds its own)
      const countBaseFilters = buildAllFilters('all', entity, search, dFrom, dTo);
      // Fetch data + stat counts in parallel
      const [dataResult, createdResult, updatedResult, deletedResult, statusResult] = await Promise.all([auditLogApi.list({
        filters: dataFilters,
        offset: offset > 0 ? offset : undefined
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'created'
        }],
        only_count: true
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'updated'
        }],
        only_count: true
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'deleted'
        }],
        only_count: true
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'status_changed'
        }],
        only_count: true
      })]);
      const logs = dataResult.data;
      // Sort by created timestamp descending (newest first)
      logs.sort((a, b) => {
        const tsA = Number(a.created) || 0;
        const tsB = Number(b.created) || 0;
        return tsB - tsA;
      });
      setAuditLogs(logs);
      setTotalCount(dataResult.total_count);
      setCreatedCount(createdResult.total_count);
      setUpdatedCount(updatedResult.total_count);
      setDeletedCount(deletedResult.total_count);
      setStatusChangedCount(statusResult.total_count);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  // Initial load + reload on scope change
  useEffect(() => {
    loadAuditLogs(1, activeAction, activeEntity, activeSearch, activeDateFrom, activeDateTo);
  }, [currentWorkspace.name, currentSubRegion]);
  const handleApplyFilters = () => {
    setActiveSearch(searchQuery.trim());
    setActiveAction(filterAction);
    setActiveEntity(filterEntity);
    setActiveDateFrom(dateFrom);
    setActiveDateTo(dateTo);
    loadAuditLogs(1, filterAction, filterEntity, searchQuery.trim(), dateFrom, dateTo);
  };
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterAction('all');
    setFilterEntity('all');
    setDateFrom('');
    setDateTo('');
    setActiveSearch('');
    setActiveAction('all');
    setActiveEntity('all');
    setActiveDateFrom('');
    setActiveDateTo('');
    loadAuditLogs(1, 'all', 'all', '', '', '');
  };
  const hasActiveFilters = activeSearch !== '' || activeAction !== 'all' || activeEntity !== 'all' || activeDateFrom !== '' || activeDateTo !== '';
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    loadAuditLogs(page);
  };
  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEvents(newExpanded);
  };
  const handleExportCSV = () => {
    if (auditLogs.length === 0) return;
    const headers = ['Date', 'Action', 'Entity Type', 'Entity Name', 'Summary', 'User', 'Email', 'Role', 'IP Address'];
    const csvContent = [headers.join(','), ...auditLogs.map(e => [`"${e.created ? new Date(Number(e.created) * 1000).toISOString() : ''}"`, `"${e.Action_Type || ''}"`, `"${e.Entity_Type || ''}"`, `"${(e.Entity_Name || '').replace(/"/g, '""')}"`, `"${(e.Summary || '').replace(/"/g, '""')}"`, `"${(e.User_Name || '').replace(/"/g, '""')}"`, `"${(e.User_Email || '').replace(/"/g, '""')}"`, `"${(e.User_Role || '').replace(/"/g, '""')}"`, `"${e.IP_Address || ''}"`].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const parseJSON = (str?: string) => {
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };
  const allCount = createdCount + updatedCount + deletedCount + statusChangedCount;
  const paginationPanel = showPagination ? <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3" data-id="element-2578" data-mp="2ow1f6k5agx9m">
      <div className="text-sm text-gray-600" data-id="element-2579" data-mp="2ttt6bby40fcr">
        Showing {(currentPage - 1) * PAGE_SIZE + 1}–
        {Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
        {totalCount.toLocaleString()} events
      </div>
      <div className="flex items-center gap-2" data-id="element-2580" data-mp="3mdrqidvlwdym">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2581" data-mp="1hwg10wi8kr">
          <ChevronLeft className="h-4 w-4" data-id="element-2582" data-mp="3hcr24jl1lb12" />
          Previous
        </button>
        <span className="text-sm text-gray-700 px-2" data-id="element-2583" data-mp="3damafqe5moh2">
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2584" data-mp="38g9p1at4b32o">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-2585" data-mp="2pkrzxfhb5bzz" />
        </button>
      </div>
    </div> : null;
  return <div className="space-y-6" data-id="element-2586" data-mp="150oa47l791qa">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2587" data-mp="153iww5roqymv">
        <div className="min-w-0" data-id="element-2588" data-mp="1954eu5j2u33x">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2589" data-mp="1e2e2pgqti6pn">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2590" data-mp="1e0be915bcln1">
            Complete history of all changes made in your workspace.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-2591" data-mp="lei4g7llo6qr">
          <button onClick={() => loadAuditLogs(currentPage)} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2592" data-mp="lbm7omlhno3w">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-2593" data-mp="104699iszpnj8" />
            <span className="hidden sm:inline" data-id="element-2594" data-mp="2mpdeiq3cvlo">Refresh</span>
          </button>
          <button onClick={handleExportCSV} disabled={auditLogs.length === 0 || loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2595" data-mp="7kr5tu9eq79t">
            <Download className="h-4 w-4" data-id="element-2596" data-mp="2j2lz8dydiqng" />
            <span data-id="element-2597" data-mp="2eab0vfja7cx0">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4" data-id="element-2598" data-mp="2sz748zae81ra">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-2599" data-mp="2t11rwld1u7z2">
          <div className="relative flex-1 min-w-0" data-id="element-2600" data-mp="2nzm3qkfkrlcg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2601" data-mp="320f3bjkqh6mf" />
            <input type="text" placeholder="Search by entity name, user, summary..." className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') handleApplyFilters();
          }} data-id="element-2602" data-mp="2x128abjpkt8r" />
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap" data-id="element-2603" data-mp="35ht19fhlhbjz">
            <select className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" value={filterAction} onChange={e => setFilterAction(e.target.value)} data-id="element-2604" data-mp="35fqdl23wzuif">
              <option value="all" data-id="element-2605" data-mp="30jj4x1k4w4ws">All Actions</option>
              {ACTION_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-2606" data-mp="2mka0ez5aiz8n">
                  {opt.label}
                </option>)}
            </select>
            <select className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" value={filterEntity} onChange={e => setFilterEntity(e.target.value)} data-id="element-2607" data-mp="krlu2gxcnvon">
              <option value="all" data-id="element-2608" data-mp="1xrf956gbwlco">All Entities</option>
              {ENTITY_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-2609" data-mp="1sppxppp725o2">
                  {opt.label}
                </option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-2610" data-mp="1jrnzfhwuhr4h">
          <div className="flex items-center gap-2 text-sm" data-id="element-2611" data-mp="2ggvblzbkqnvq">
            <span className="text-gray-500" data-id="element-2612" data-mp="2gig7bs9mkcv6">Date Range:</span>
            <input type="date" className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-700" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-2613" data-mp="2cgbdasf9le80" />
            <span className="text-gray-400" data-id="element-2614" data-mp="22ne2l8giktjy">to</span>
            <input type="date" className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-700" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-2615" data-mp="305nuofkqwsle" />
          </div>
          <div className="flex items-center gap-2 sm:ml-auto" data-id="element-2616" data-mp="37o3xb7uam1sb">
            {hasActiveFilters && <button onClick={handleClearFilters} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2617" data-mp="3cn84jsxnppg0">
                <X className="h-3.5 w-3.5" data-id="element-2618" data-mp="3qmzn86z4bu0c" />
                Clear
              </button>}
            <button onClick={handleApplyFilters} disabled={loading} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-2619" data-mp="3vdy9roap9oc1">
              <Filter className="h-3.5 w-3.5" data-id="element-2620" data-mp="86xbohmj735h" />
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-2621" data-mp="yi8nvvq53tmm">
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2622" data-mp="tk74f9osxsam">
          <p className="text-2xl font-semibold text-gray-900" data-id="element-2623" data-mp="tmb7xqisgpad">
            {loading ? '-' : allCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2624" data-mp="13hlbwjsqz6qs">Total Events</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2625" data-mp="fsahrl5wkalm">
          <p className="text-2xl font-semibold text-green-600" data-id="element-2626" data-mp="fqfwg5qpygh0">
            {loading ? '-' : createdCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2627" data-mp="pivet45xh72f">Created</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2628" data-mp="krn5e84lllp9">
          <p className="text-2xl font-semibold text-blue-600" data-id="element-2629" data-mp="kpk3apq68246">
            {loading ? '-' : updatedCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2630" data-mp="3o8luiwmwc0al">Updated</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2631" data-mp="3o6j080epnl02">
          <p className="text-2xl font-semibold text-red-600" data-id="element-2632" data-mp="23p077341n0h9">
            {loading ? '-' : deletedCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2633" data-mp="1tsezga0n3b9q">Deleted</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 col-span-2 sm:col-span-1" data-id="element-2634" data-mp="1tqal0y546tv7">
          <p className="text-2xl font-semibold text-yellow-600" data-id="element-2635" data-mp="1yovmf0w3rjl8">
            {loading ? '-' : statusChangedCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2636" data-mp="2rg5hl2r7t0k6">Status Changed</p>
        </div>
      </div>

      {/* Error State */}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md" data-id="element-2637" data-mp="2di76c686xk8w">
          {error}
        </div>}

      {/* Top Pagination */}
      {paginationPanel}

      {/* Events List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2638" data-mp="123bu0qx0nuvi">
        {loading ? <div className="divide-y divide-gray-200" data-id="element-2639" data-mp="8budnt9sdkqp">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="p-4 flex items-start gap-4 animate-pulse" data-id="element-2640" data-mp="cdflh07gr8u1">
                <div className="h-10 w-10 bg-gray-200 rounded-full flex-shrink-0" data-id="element-2641" data-mp="cbbiqllawfvc"></div>
                <div className="flex-1 space-y-2" data-id="element-2642" data-mp="3uispwz4d74ct">
                  <div className="h-4 bg-gray-200 rounded w-1/3" data-id="element-2643" data-mp="3c7y3d6hjseq"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4" data-id="element-2644" data-mp="3kt94blp9owe9"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2645" data-mp="3krefvxesem5f"></div>
              </div>)}
          </div> : auditLogs.length === 0 ? <div className="p-12 text-center" data-id="element-2646" data-mp="3btuemogxcput">
            <Clock className="mx-auto h-12 w-12 text-gray-400" data-id="element-2647" data-mp="3bqq31614qru4" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-2648" data-mp="3gs5navvhgubg">
              No events found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2649" data-mp="31wgb1pg3nvjx">
              {hasActiveFilters ? 'Try adjusting your filters.' : 'No audit events have been recorded yet.'}
            </p>
          </div> : <div className="divide-y divide-gray-200" data-id="element-2650" data-mp="2j6kx3sol0kvz">
            {auditLogs.map(event => {
          const EntityIcon = entityIcons[event.Entity_Type] || FileText;
          const actionStyle = actionColors[event.Action_Type] || actionColors.updated;
          const isExpanded = expandedEvents.has(event.ID);
          const changes = parseJSON(event.Changes_JSON);
          const metadata = parseJSON(event.Metadata_JSON);
          const hasDetails = changes && changes.length > 0 || metadata;
          return <div key={event.ID} className="hover:bg-gray-50" data-id="element-2651" data-mp="3fyg8dafvf1jh">
                  <div className={`p-4 flex items-start gap-3 sm:gap-4 ${hasDetails ? 'cursor-pointer' : ''}`} onClick={() => hasDetails && toggleExpanded(event.ID)} data-id="element-2652" data-mp="3ayjzoxw8alxl">
                    {/* Icon */}
                    <div className={`flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center ${actionStyle.bg}`} data-id="element-2653" data-mp="3rywmfwl0ipo4">
                      <EntityIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${actionStyle.text}`} data-id="element-2654" data-mp="5revrjzzszsq" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-2655" data-mp="fh848c4u8ysr">
                      <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2656" data-mp="fgomavf6uaeq">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${actionStyle.bg} ${actionStyle.text}`} data-id="element-2657" data-mp="jhs1dfpew2ew">
                          {(event.Action_Type || 'unknown').replace('_', ' ')}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap" data-id="element-2658" data-mp="2qau9iy8d1joh">
                          {(event.Entity_Type || 'unknown').replace('_', ' ')}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate" data-id="element-2659" data-mp="2ixuxbsp2k2r3">
                          {event.Entity_Name}
                        </span>
                      </div>

                      {event.Summary && <p className="text-sm text-gray-600 mb-1" data-id="element-2660" data-mp="291i2uslp9kva">
                          {event.Summary}
                        </p>}

                      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap" data-id="element-2661" data-mp="31oco3rqcco9t">
                        <span className="flex items-center gap-1 whitespace-nowrap" data-id="element-2662" data-mp="2wozzb1fwt74l">
                          <User className="h-3 w-3 flex-shrink-0" data-id="element-2663" data-mp="2rqoqmu1cp2ny" />
                          <span className="font-medium text-gray-700" data-id="element-2664" data-mp="2rsj11eezxcds">
                            {event.User_Name || 'System'}
                          </span>
                        </span>
                        {event.User_Email && <>
                            <span className="hidden sm:inline" data-id="element-2665" data-mp="354p23lrhvg76">•</span>
                            <span className="hidden sm:inline truncate" data-id="element-2666" data-mp="39wp37hjawf5c">
                              {event.User_Email}
                            </span>
                          </>}
                        {event.User_Role && <>
                            <span className="hidden sm:inline" data-id="element-2667" data-mp="3nw84prhlws8o">•</span>
                            <span className="hidden sm:inline px-1.5 py-0.5 bg-gray-100 rounded text-[10px]" data-id="element-2668" data-mp="3nu4017bvvp7c">
                              {event.User_Role}
                            </span>
                          </>}
                      </div>
                    </div>

                    {/* Timestamp & Expand */}
                    <div className="flex items-center gap-2 text-right flex-shrink-0" data-id="element-2669" data-mp="22ic9d5o4qjhh">
                      {(() => {
                  const ts = formatTimestamp(event.created);
                  return <>
                            <div className="hidden sm:block" data-id="element-2670" data-mp="plwnoyvu6sor">
                              <div className="text-sm text-gray-900 whitespace-nowrap" data-id="element-2671" data-mp="kk7b3esyu6yk">
                                {ts.date}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-2672" data-mp="uin4jofgtsb8">
                                {ts.time}
                              </div>
                            </div>
                            <div className="sm:hidden text-xs text-gray-500 whitespace-nowrap" data-id="element-2673" data-mp="1uhjt1oew3be">
                              {ts.date}
                            </div>
                          </>;
                })()}
                      {hasDetails && <div className="text-gray-400 flex-shrink-0 ml-2" data-id="element-2674" data-mp="atcmw4w5sxah">
                          {isExpanded ? <ChevronDown className="h-4 w-4" data-id="element-2675" data-mp="3f9nhbkmxnrn4" /> : <ChevronRight className="h-4 w-4" data-id="element-2676" data-mp="1i916ovecutrg" />}
                        </div>}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && hasDetails && <div className="px-4 pb-4 ml-11 sm:ml-14 border-l-2 border-gray-100" data-id="element-2677" data-mp="1wxny3vh9bbz1">
                      {changes && changes.length > 0 && <div className="bg-gray-50 rounded-md p-3 space-y-2 border border-gray-200" data-id="element-2678" data-mp="zb71ytk9f4ev">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-2679" data-mp="149rtm4lg4frp">
                            Changes
                          </p>
                          <div className="overflow-x-auto" data-id="element-2680" data-mp="1e7pfwmjqw2ho">
                            <table className="min-w-full text-sm text-left" data-id="element-2681" data-mp="1e538cmwutmzg">
                              <thead className="text-xs text-gray-500 border-b border-gray-200" data-id="element-2682" data-mp="2kmqaoox1aemo">
                                <tr data-id="element-2683" data-mp="2pkrwhhlugs1u">
                                  <th className="pb-2 font-medium" data-id="element-2684" data-mp="dyzqyw8pwhdt">Field</th>
                                  <th className="pb-2 font-medium" data-id="element-2685" data-mp="e12ffbu822gf">
                                    Old Value
                                  </th>
                                  <th className="pb-2 font-medium" data-id="element-2686" data-mp="48ejskeza4ja">
                                    New Value
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100" data-id="element-2687" data-mp="3qhxz2illca4q">
                                {changes.map((change: any, idx: number) => <tr key={idx} data-id="element-2688" data-mp="3hfges9phxrkd" data-mpi={idx}>
                                    <td className="py-2 pr-4 font-medium text-gray-700 align-top" data-id="element-2689" data-mp="3hfyghluxgxzo">
                                      {change.field}
                                    </td>
                                    <td className="py-2 pr-4 text-red-600 align-top break-all max-w-xs" data-id="element-2690" data-mp="1fnthxrixb2fz">
                                      {change.oldValue === null || change.oldValue === undefined || change.oldValue === '' ? <span className="text-gray-400 italic" data-id="element-2691" data-mp="oi7khvt39kb4">
                                          (empty)
                                        </span> : typeof change.oldValue === 'object' ? JSON.stringify(change.oldValue) : String(change.oldValue)}
                                    </td>
                                    <td className="py-2 text-green-600 align-top break-all max-w-xs" data-id="element-2692" data-mp="3rywl715zpzsv">
                                      {change.newValue === null || change.newValue === undefined || change.newValue === '' ? <span className="text-gray-400 italic" data-id="element-2693" data-mp="3arf44n5jqha3">
                                          (empty)
                                        </span> : typeof change.newValue === 'object' ? JSON.stringify(change.newValue) : String(change.newValue)}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div>
                        </div>}

                      {metadata && Object.keys(metadata).length > 0 && <div className="bg-gray-50 rounded-md p-3 mt-3 border border-gray-200" data-id="element-2694" data-mp="241x65wqpv4s2">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-2695" data-mp="1d04af2r8l5f5">
                            Additional Info
                          </p>
                          <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap break-all" data-id="element-2696" data-mp="190n3e4w5w07v">
                            {JSON.stringify(metadata, null, 2)}
                          </pre>
                        </div>}

                      {event.IP_Address && <div className="mt-3 text-xs text-gray-500 flex items-center gap-1" data-id="element-2697" data-mp="u79wmc5cnjci">
                          <span className="font-medium" data-id="element-2698" data-mp="pcw6op9gi4f8">IP Address:</span>{' '}
                          {event.IP_Address}
                        </div>}
                    </div>}
                </div>;
        })}
          </div>}
      </div>

      {/* Bottom Pagination */}
      {paginationPanel}
    </div>;
}